import React, { useState } from "react";
import { Card, Tabs, Typography, Slider, Space, Row, Col, Button, Steps } from "antd";

const { Title, Paragraph, Text } = Typography;

export default function DiffusionExplainer() {
  const [noiseLevel, setNoiseLevel] = useState(100);
  const [selectedPrompt, setSelectedPrompt] = useState("");

  const handlePromptClick = (prompt) => {
    setSelectedPrompt(prompt);
    navigator.clipboard
      .writeText(prompt)
      .then(() => {
        // Prompt copied to clipboard
      })
      .catch((err) => {
        console.error("Could not copy text: ", err);
      });
  };

  const examplePrompts = [
    "A magical castle floating in the clouds at sunset",
    "A cute robot playing with a puppy in a garden",
    "An astronaut riding a skateboard on Mars",
    "A cozy treehouse with glowing windows at night",
    "A dragon made of crystals sleeping on treasure",
    "Underwater city with colorful fish and mermaids",
    "An airship flying through rainbow clouds",
    "Giant friendly monster having tea party with kids",
    "Flying cars at sunset along side iron man"
  ];

  const modelLinks = [
    {
      name: "Stable Diffusion",
      url: "https://stablediffusionweb.com",
      type: "Open Source",
      desc: "Free and open source image generation"
    },
    {
      name: "DALL-E 3 by OpenAI",
      url: "https://openai.com/dall-e-3",
      type: "Premium",
      desc: "High-quality AI art generation"
    },
    { 
      name: "Midjourney", 
      url: "https://www.midjourney.com", 
      type: "Premium",
      desc: "Professional AI art creation"
    },
    { 
      name: "Leonardo.ai", 
      url: "https://leonardo.ai", 
      type: "Free & Premium",
      desc: "AI art with fine-tuned control"
    },
    {
      name: "Playground AI",
      url: "https://playgroundai.com",
      type: "Free & Premium",
      desc: "Easy-to-use AI image generator"
    },
    {
      name: "Hugging Face Spaces",
      url: "https://huggingface.co/spaces",
      type: "Open Source",
      desc: "Community AI models and demos"
    },
  ];

  const renderIntro = () => (
    <Space direction="vertical" size="large" style={{ width: "100%" }}>
      <Card title={<span style={{ color: "#0071e3" }}>What's a Diffusion Model?</span>}>
        <Paragraph style={{ fontSize: "16px" }}>
          Imagine you have a photo that's covered in fog (noise). A diffusion
          model is like a magical eraser that slowly removes the fog to reveal a
          beautiful picture underneath!
        </Paragraph>

        <Paragraph style={{ fontSize: "16px", marginBottom: "16px" }}>
          <Text strong>Here's the cool part:</Text>
        </Paragraph>

        <div style={{ paddingLeft: "16px" }}>
          <Paragraph style={{ marginBottom: "8px" }}>
            <Text strong>1.</Text> You describe what you want: "a dragon eating pizza"
          </Paragraph>
          <Paragraph style={{ marginBottom: "8px" }}>
            <Text strong>2.</Text> The AI starts with pure static (like TV snow)
          </Paragraph>
          <Paragraph style={{ marginBottom: "8px" }}>
            <Text strong>3.</Text> It slowly removes the noise, step by step
          </Paragraph>
          <Paragraph style={{ marginBottom: 0 }}>
            <Text strong>4.</Text> After many steps, your image appears!
          </Paragraph>
        </div>
      </Card>

      <Card title={<span style={{ color: "#0071e3" }}>Think of it like...</span>}>
        <Row gutter={[16, 16]}>
          <Col xs={24} md={12}>
            <div style={{ padding: "16px", borderRadius: "8px", border: "1px solid #0071e3", }}>
              <Paragraph style={{ fontWeight: 600, marginBottom: "8px" }}>
                Sculpting from Clay
              </Paragraph>
              <Paragraph style={{ marginBottom: 0 }}>
                Starting with a messy blob and gradually shaping it into a statue
              </Paragraph>
            </div>
          </Col>
          <Col xs={24} md={12}>
            <div style={{ padding: "16px", borderRadius: "8px", border: "1px solid #0071e3", }}>
              <Paragraph style={{ fontWeight: 600, marginBottom: "8px" }}>
                Cleaning a Foggy Window
              </Paragraph>
              <Paragraph style={{ marginBottom: 0 }}>
                Wiping away steam to see the view outside
              </Paragraph>
            </div>
          </Col>
        </Row>
      </Card>
    </Space>
  );

  const renderDemo = () => (
    <Space direction="vertical" size="large" style={{ width: "100%" }}>
      <Card title={<span style={{ color: "#0071e3" }}>The Denoising Process</span>}>
        <Paragraph style={{ marginBottom: "16px" }}>
          Move the slider towards the left to see how the AI removes noise step by step!
        </Paragraph>

        <div
          style={{
            backgroundColor: "#f5f5f5",
            padding: "32px",
            borderRadius: "8px",
            marginBottom: "16px",
            position: "relative",
            overflow: "hidden",
            height: "300px",
          }}
        >
          {/* Noise effect */}
          <div
            style={{
              position: "absolute",
              inset: "0",
              background: "linear-gradient(to bottom right, #a78bfa, #f472b6)",
              opacity: Math.max(0.1, noiseLevel / 100),
              transition: "opacity 0.2s",
            }}
          >
            {Array.from({ length: 500 }).map((_, i) => (
              <div
                key={i}
                style={{
                  position: "absolute",
                  backgroundColor: "white",
                  borderRadius: "50%",
                  width: Math.random() * 4 + "px",
                  height: Math.random() * 4 + "px",
                  left: Math.random() * 100 + "%",
                  top: Math.random() * 100 + "%",
                  opacity: noiseLevel / 100,
                }}
              />
            ))}
          </div>

          {/* Revealed image */}
          <div
            style={{
              position: "absolute",
              inset: "0",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              opacity: Math.min(1, 1 - Math.max(0, noiseLevel - 20) / 70),
              transition: "opacity 0.5s",
            }}
          >
            <div style={{ textAlign: "center" }}>
              <div style={{ fontSize: "64px", marginBottom: "8px" }}>🐉</div>
              <Text style={{ fontSize: "24px", fontWeight: 600 }}>
                A Dragon!
              </Text>
            </div>
          </div>
        </div>

        <Paragraph style={{ fontWeight: 600, marginBottom: "8px" }}>
          Noise Level: {noiseLevel}%
        </Paragraph>
        <Slider
          min={0}
          max={100}
          value={noiseLevel}
          onChange={setNoiseLevel}
          // marks={{
          //   0: "Clear",
          //   50: "Medium",
          //   100: "Full Noise"
          // }}
        />

<Paragraph
              style={{ marginTop: "16px", marginBottom: 0, fontSize: "14px" }}
            >
              Full Noise → Medium → Clear
            </Paragraph>
        <Paragraph type="secondary" style={{ marginTop: "8px", marginBottom: 0 }}>
          Real diffusion models do this in 20-50 steps, each removing a bit more noise!
        </Paragraph>
      </Card>
    </Space>
  );

  const renderHistory = () => (
    <Space direction="vertical" size="large" style={{ width: "100%" }}>
      <Card title={<span style={{ color: "#0071e3" }}>2015 - The Beginning</span>}>
        <Paragraph style={{ marginBottom: 0 }}>
          Researchers discover that training neural networks to remove noise can create amazing images. 
          The first diffusion models are born!
        </Paragraph>
      </Card>

      <Card title={<span style={{ color: "#0071e3" }}>2020 - Getting Better</span>}>
        <Paragraph style={{ marginBottom: 0 }}>
          Diffusion models start producing high-quality images that rival GANs. 
          Research explodes as scientists realize the potential.
        </Paragraph>
      </Card>

      <Card title={<span style={{ color: "#0071e3" }}>2022 - The Revolution</span>}>
        <Paragraph style={{ marginBottom: 0 }}>
          <Text strong>Stable Diffusion</Text> is released to the public for free! 
          Suddenly, anyone can create AI art. DALL-E 2 and Midjourney also launch, 
          making AI art mainstream.
        </Paragraph>
      </Card>

      <Card title={<span style={{ color: "#0071e3" }}>Today - Everywhere</span>}>
        <Paragraph style={{ marginBottom: 0 }}>
          Diffusion models power most AI art tools. They're used in advertising, 
          game design, movies, and by millions of creative people worldwide. 
          The quality keeps improving every month!
        </Paragraph>
      </Card>
    </Space>
  );

  const renderComparison = () => (
    <Space direction="vertical" size="large" style={{ width: "100%" }}>
      <Row gutter={[16, 16]}>
        <Col xs={24} md={8}>
          <Card title={<span style={{ color: "#0071e3" }}>Diffusion Models</span>}>
            <Paragraph style={{ marginBottom: "8px" }}>
              <Text strong>Strengths:</Text>
            </Paragraph>
            <ul style={{ marginBottom: "12px", paddingLeft: "20px" }}>
              <li>Highest quality images</li>
              <li>Great variety and creativity</li>
              <li>Very stable training</li>
            </ul>
            <Paragraph style={{ marginBottom: "8px" }}>
              <Text strong>Weakness:</Text>
            </Paragraph>
            <ul style={{ marginBottom: "12px", paddingLeft: "20px" }}>
              <li>Slower to generate (20-50 steps needed)</li>
            </ul>
          </Card>
        </Col>

        <Col xs={24} md={8}>
          <Card title={<span style={{ color: "#0071e3" }}>GANs</span>}>
            <Paragraph style={{ marginBottom: "8px" }}>
              <Text strong>Strengths:</Text>
            </Paragraph>
            <ul style={{ marginBottom: "12px", paddingLeft: "20px" }}>
              <li>Very fast generation</li>
              <li>Great for faces</li>
              <li>Real-time possible</li>
            </ul>
            <Paragraph style={{ marginBottom: "8px" }}>
              <Text strong>Weakness:</Text>
            </Paragraph>
            <ul style={{ marginBottom: "12px", paddingLeft: "20px" }}>
              <li>Training can be unstable and less variety</li>
            </ul>
          </Card>
        </Col>

        <Col xs={24} md={8}>
          <Card title={<span style={{ color: "#0071e3" }}>VAEs</span>}>
            <Paragraph style={{ marginBottom: "8px" }}>
              <Text strong>Strengths:</Text>
            </Paragraph>
            <ul style={{ marginBottom: "12px", paddingLeft: "20px" }}>
              <li>Smooth interpolation</li>
              <li>Good for compression</li>
              <li>Stable training</li>
            </ul>
            <Paragraph style={{ marginBottom: "8px" }}>
              <Text strong>Weakness:</Text>
            </Paragraph>
            <ul style={{ marginBottom: "12px", paddingLeft: "20px" }}>
              <li>Images are often blurry</li>
            </ul>
          </Card>
        </Col>
      </Row>

      <Card title={<span style={{ color: "#0071e3" }}>The Winner?</span>}>
        <Paragraph style={{ marginBottom: 0, textAlign: "justify" }}>
          For creating amazing art from text descriptions, Diffusion Models are 
          currently the best! That's why tools like Stable Diffusion, DALL-E, and Midjourney all 
          use diffusion technology.
        </Paragraph>
      </Card>
    </Space>
  );

  const renderPrompts = () => (
    <Space direction="vertical" size="large" style={{ width: "100%" }}>
      {/* <Card title={<span style={{ color: "#0071e3" }}>Try These Prompts</span>}>
        <Paragraph style={{ marginBottom: "16px" }}>
          Click any prompt to copy it to your clipboard, then paste it into an AI art tool! Refer the next section for the list of AI art websites.
        </Paragraph>

        <Space direction="vertical" size="middle" style={{ width: "100%" }}>
          {examplePrompts.map((prompt, idx) => (
            <div
              key={idx}
              onClick={() => handlePromptClick(prompt)}
              style={{
                padding: "16px",
                border: selectedPrompt === prompt ? "2px solid #0071e3" : "1px solid #d9d9d9",
                borderRadius: "8px",
                cursor: "pointer",
                backgroundColor: selectedPrompt === prompt ? "#f0f5ff" : "white",
                transition: "all 0.2s"
              }}
            >
              <Text>{prompt}</Text>
              {selectedPrompt === prompt && (
                <Text type="secondary" style={{ marginLeft: "8px" }}>✓ Copied!</Text>
              )}
            </div>
          ))}
        </Space>
      </Card> */}

      <Card title={<span style={{ color: "#0071e3" }}>Try These Prompts</span>}>
  <Paragraph style={{ marginBottom: "16px" }}>
    Click any prompt to copy it to your clipboard, then paste it into an AI art tool! Refer the next section for the list of AI art websites.
  </Paragraph>

  <Row gutter={[16, 16]} justify="center">
    {examplePrompts.map((prompt, idx) => (
      <Col xs={24} sm={12} md={8} key={idx}>
        <div
          onClick={() => handlePromptClick(prompt)}
          style={{
            padding: "16px",
            border: selectedPrompt === prompt ? "2px solid #0071e3" : "1px solid #d9d9d9",
            borderRadius: "8px",
            cursor: "pointer",
            backgroundColor: selectedPrompt === prompt ? "#f0f5ff" : "white",
            transition: "all 0.2s"
          }}
        >
          <Text>{prompt}</Text>
          {selectedPrompt === prompt && (
            <Text type="secondary" style={{ marginLeft: "8px" }}>✓ Copied!</Text>
          )}
        </div>
      </Col>
    ))}
  </Row>
</Card>


      <Card title={<span style={{ color: "#0071e3" }}>Try These AI Art Tools</span>}>
        <Paragraph style={{ fontSize: "16px", marginBottom: "16px" }}>
          Start creating your own AI art with these platforms:
        </Paragraph>

        <Space direction="vertical" size="middle" style={{ width: "100%" }}>
          {modelLinks.map((link, idx) => (
            <div
              key={idx}
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                padding: "12px",
                border: "1px solid #d9d9d9",
                borderRadius: "8px",
                backgroundColor: "white"
              }}
            >
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 600, marginBottom: "4px" }}>
                  {link.name} <Text type="secondary">({link.type})</Text>
                </div>
                <div style={{ fontSize: "14px", color: "#666" }}>{link.desc}</div>
              </div>
              <Button
                href={link.url}
                target="_blank"
                rel="noopener noreferrer"
                type="primary"
                style={{ marginLeft: "16px" }}
              >
                Visit
              </Button>
            </div>
          ))}
        </Space>
      </Card>

      <Card title={<span style={{ color: "#0071e3" }}>Pro Tips for Better Prompts</span>}>
        <ul style={{ paddingLeft: "20px", marginBottom: 0 }}>
          <li style={{ marginBottom: "8px" }}>
            <Text strong>Be specific:</Text> "golden retriever puppy" is better than "dog"
          </li>
          <li style={{ marginBottom: "8px" }}>
            <Text strong>Add style:</Text> "digital art", "oil painting", "photorealistic"
          </li>
          <li style={{ marginBottom: "8px" }}>
            <Text strong>Set the mood:</Text> "at sunset", "in moonlight", "dramatic lighting"
          </li>
          <li style={{ marginBottom: 0 }}>
            <Text strong>Describe details:</Text> colors, textures, emotions, atmosphere
          </li>
        </ul>
      </Card>
    </Space>
  );

  const renderRealWorld = () => (
    <Space direction="vertical" size="large" style={{ width: "100%" }}>
      <Paragraph style={{ fontSize: "16px" }}>
        Diffusion models are transforming creative industries and being used by millions worldwide.
      </Paragraph>

      <Row gutter={[16, 16]}>
        <Col xs={24} md={8}>
          <Card title={<span style={{ color: "#0071e3" }}>Art & Entertainment</span>}>
            <ul style={{ paddingLeft: "20px", marginBottom: 0 }}>
              <li>Movie posters and concept art</li>
              <li>Video game backgrounds and characters</li>
              <li>Album covers and music videos</li>
              <li>Book illustrations and covers</li>
              <li>Comic book art and storyboards</li>
            </ul>
          </Card>
        </Col>

        <Col xs={24} md={8}>
          <Card title={<span style={{ color: "#0071e3" }}>Business & Marketing</span>}>
            <ul style={{ paddingLeft: "20px", marginBottom: 0 }}>
              <li>Social media content</li>
              <li>Advertising campaigns</li>
              <li>Product mockups and prototypes</li>
              <li>Website graphics and banners</li>
              <li>Brand identity design</li>
            </ul>
          </Card>
        </Col>

        <Col xs={24} md={8}>
          <Card title={<span style={{ color: "#0071e3" }}>Education & Personal</span>}>
            <ul style={{ paddingLeft: "20px", marginBottom: 0 }}>
              <li>Educational illustrations</li>
              <li>Presentation visuals</li>
              <li>Custom wallpapers and art</li>
              <li>Interior design concepts</li>
              <li>Fashion design exploration</li>
            </ul>
          </Card>
        </Col>
      </Row>

      <Card title={<span style={{ color: "#0071e3" }}>The Future is Bright</span>}>
        <Paragraph style={{ fontSize: "16px", marginBottom: "12px" }}>
          AI art is just getting started! In the near future, we'll see:
        </Paragraph>
        <ul style={{ paddingLeft: "20px", marginBottom: 0 }}>
          <li style={{ marginBottom: "8px" }}>
            <Text strong>Better quality:</Text> Even more realistic and detailed images
          </li>
          <li style={{ marginBottom: "8px" }}>
            <Text strong>Faster generation:</Text> Create images in seconds instead of minutes
          </li>
          <li style={{ marginBottom: "8px" }}>
            <Text strong>Video generation:</Text> Full AI-generated movies and animations
          </li>
          <li style={{ marginBottom: "8px" }}>
            <Text strong>3D models:</Text> AI creating entire 3D worlds and objects
          </li>
          <li style={{ marginBottom: 0 }}>
            <Text strong>Personal style:</Text> AI that learns and adapts to your unique artistic vision
          </li>
        </ul>
      </Card>
    </Space>
  );

  const tabItems = [
    {
      key: 'intro',
      label: 'What is Diffusion?',
      children: renderIntro(),
    },
    {
      key: 'history',
      label: 'The Story',
      children: renderHistory(),
    },
    {
      key: 'demo',
      label: 'Interactive Demo',
      children: renderDemo(),
    },
    {
      key: 'comparison',
      label: 'AI Showdown',
      children: renderComparison(),
    },
    {
      key: 'prompts',
      label: 'Cool Prompts',
      children: renderPrompts(),
    },
    {
      key: 'realworld',
      label: 'Real Uses',
      children: renderRealWorld(),
    },
  ];

  return (
    <div className="page-container">
      <header style={{ textAlign: "center", marginBottom: "32px" }} className="fade-in-down">
        <Title level={1}>Diffusion Models</Title>
        <Paragraph style={{ fontSize: "18px" }}>
          The Magic Behind AI Art Generation
        </Paragraph>
      </header>

      <Tabs
        defaultActiveKey="intro"
        items={tabItems}
        size="large"
      />
    </div>
  );
}